import { CoutsExposition } from '../pilotage/CoutsExposition.js';
// Fiche exposition, transitions et accès aux gestes des modules actifs.
import { EvenementsExposition } from '../evenements/EvenementsLies.js';
import { NouvelleSelection } from '../presentation/NouvelleSelection.js';
import { IndicateurFraicheur } from '../racine/IndicateurFraicheur.js';
import { SectionPresse } from '../presse/SectionPresse.js';
import { texteEnLangue } from '@artosera/shared';
import {
  Badge,
  Button,
  Dialog,
  DropdownMenu,
  Select,
  Tabs,
  type DropdownMenuEntry,
} from '@artosera/ui';
import { formatDateRange, type EtatExposition } from '@artosera/shared';
import { ChevronDown, Download, FileStack } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { modeDemonstrationActif } from '../../api/demo.js';
import { estApiError } from '../../api/errors.js';
import {
  documentsGalerieApi,
  type ReponsePreparationDocuments,
} from '../../api/documentsGalerie.js';
import {
  expositionsApi,
  useArchiverExposition,
  useRestaurerExposition,
  useChangerEtatExposition,
  useExposition,
} from '../../api/expositions.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ActualitesTab } from './tabs/ActualitesTab.js';
import { InformationsTab } from './tabs/InformationsTab.js';
import { MediasTab } from './tabs/MediasTab.js';
import { OeuvresArtistesTab } from './tabs/OeuvresArtistesTab.js';
import { PortfolioExposition } from './PortfolioExposition.js';
import { CatalogueVisiteTab } from './tabs/CatalogueVisiteTab.js';
import { autresEtats, etatSuivant } from './transitionsExposition.js';
import { VisuelExposition } from './VisuelExposition.js';
import { teinteDeLEtat } from './etatUi.js';
import { enregistrerBlob, ouvrirUrlSignee } from '../../api/client.js';
import {
  capturerPortee,
  epoqueCourante,
  estActuelle,
  estPorteeObsolete,
  useIdentite,
} from '../../auth/identite.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { RetourGeste } from '../RetourGeste.js';
import styles from './ExpositionDetailPage.module.css';
import { LibelleOnglet } from '../module/LectureSeule.js';

/** Ordre naturel des états, pour le parcours dessiné sous le titre. */
const PARCOURS: readonly EtatExposition[] = ['brouillon', 'annoncee', 'en_cours', 'passee'];

/**
 * Fiche exposition complète (3.3, point 4, S3-d) : en-tête (type, titre,
 * dates, état posé avec ses transitions), lieu/stand, onglets (informations,
 * œuvres et artistes, médias, actualités), liste de prix du stand.
 */
export function ExpositionDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { galerieSlug, galerie, compte } = useShellContext();

  const fiche = useExposition(galerieSlug, id ?? null);
  const changerEtat = useChangerEtatExposition(galerieSlug);
  const archiver = useArchiverExposition(galerieSlug);
  const restaurer = useRestaurerExposition(galerieSlug);
  const [retour, setRetour] = useState<string | null>(null);

  const [etatDialogueOuvert, setEtatDialogueOuvert] = useState(false);
  const [etatVise, setEtatVise] = useState<EtatExposition | ''>('');
  const [erreurEtat, setErreurEtat] = useState<string | null>(null);
  const [archivageOuvert, setArchivageOuvert] = useState(false);
  const [listeDePrixErreur, setListeDePrixErreur] = useState<string | null>(null);
  const [listeDePrixEnCours, setListeDePrixEnCours] = useState(false);
  const [listeDePrixPdfEnCours, setListeDePrixPdfEnCours] = useState(false);

  const identite = useIdentite();
  const epoque = epoqueCourante();
  const peutPreparer =
    galerieAModule(galerie, 'documents-express') &&
    galerieALaction(galerie, 'exhibitions:price-list') &&
    (fiche.data?.oeuvres.length ?? 0) > 0;
  const contextePreparation = useMemo(
    () => ({ identite, epoque, galerieSlug, id, peutPreparer }),
    [identite, epoque, galerieSlug, id, peutPreparer],
  );
  const [preparation, setPreparation] = useState<{
    contexte: typeof contextePreparation;
    enCours: boolean;
    documents: ReponsePreparationDocuments | null;
    erreur: string | null;
  } | null>(null);
  // Un contexte neuf masque immédiatement tout état précédent, même avant l’effet.
  const preparationCourante = preparation?.contexte === contextePreparation ? preparation : null;
  const preparationEnCours = preparationCourante?.enCours ?? false;
  const documentsPrepares = preparationCourante?.documents ?? null;
  const preparationErreur = preparationCourante?.erreur ?? null;
  const requetePreparation = useRef(0);
  const preparationActive = useRef(false);
  const contexteActuel = useRef(contextePreparation);
  // Déclaré avant les retours loading/error : Outlet peut conserver ce composant.
  useEffect(() => {
    contexteActuel.current = contextePreparation;
    requetePreparation.current += 1;
    preparationActive.current = false;
    return () => {
      requetePreparation.current += 1;
      preparationActive.current = false;
    };
  }, [contextePreparation]);

  if (fiche.isLoading) {
    return <p className={styles.vide}>{t('commun:chargement')}</p>;
  }
  if (fiche.isError || !fiche.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(fiche.error) ? fiche.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  const { exposition, lieu, artistes, oeuvres, vuesAccrochage } = fiche.data;
  const peutEcrire = galerieALaction(galerie, 'exhibitions:write');
  const peutTelechargerListe = galerieALaction(galerie, 'exhibitions:price-list');

  const suivant = etatSuivant(exposition.etat);
  const entreesEtat: DropdownMenuEntry[] = [
    ...(suivant
      ? [
          {
            type: 'item' as const,
            item: {
              key: 'suivant',
              label: t('regie:expositions.etatMenu.prochainEtat', {
                etat: t(`regie:expositions.etats.${suivant}`),
              }),
              onSelect: () => {
                setEtatVise(suivant);
                setErreurEtat(null);
                setEtatDialogueOuvert(true);
              },
            },
          },
          { type: 'separator' as const },
        ]
      : []),
    {
      type: 'item',
      item: {
        key: 'autre',
        label: t('regie:expositions.etatMenu.autreEtat'),
        onSelect: () => {
          setEtatVise('');
          setErreurEtat(null);
          setEtatDialogueOuvert(true);
        },
      },
    },
  ];

  async function confirmerEtat(): Promise<void> {
    if (!etatVise) return;
    setErreurEtat(null);
    try {
      await changerEtat.mutateAsync({ id: exposition.id, etat: etatVise });
      setEtatDialogueOuvert(false);
    } catch (error) {
      setErreurEtat(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  async function telechargerListeDePrix(): Promise<void> {
    setListeDePrixErreur(null);
    setListeDePrixEnCours(true);
    try {
      const reponse = await dansLaPorteeCourante((appel) =>
        expositionsApi.listeDePrix(galerieSlug, exposition.id, langue, appel),
      );
      const contenu = JSON.stringify(reponse, null, 2);
      enregistrerBlob(
        new Blob([contenu], { type: 'application/json' }),
        `liste-de-prix-${exposition.slug}.json`,
      );
    } catch (error) {
      setListeDePrixErreur(
        estApiError(error) ? error.message : t('regie:expositions.listeDePrix.echec'),
      );
    } finally {
      setListeDePrixEnCours(false);
    }
  }

  async function ouvrirListeDePrixPdf(): Promise<void> {
    setListeDePrixErreur(null);
    setListeDePrixPdfEnCours(true);
    try {
      const document = await dansLaPorteeCourante((appel) =>
        documentsGalerieApi.listeDePrixExpositionPdf(galerieSlug, exposition.id, langue, appel),
      );
      ouvrirUrlSignee(document.url);
    } catch (error) {
      setListeDePrixErreur(
        estApiError(error) ? error.message : t('regie:expositions.listeDePrix.echec'),
      );
    } finally {
      setListeDePrixPdfEnCours(false);
    }
  }

  async function preparerDocuments(): Promise<void> {
    if (!peutPreparer || preparationActive.current) return;
    const capture = capturerPortee();
    const requete = ++requetePreparation.current;
    const actuelle = () =>
      estActuelle(capture) &&
      requetePreparation.current === requete &&
      contexteActuel.current === contextePreparation &&
      contextePreparation.peutPreparer;
    preparationActive.current = true;
    setPreparation({ contexte: contextePreparation, enCours: true, documents: null, erreur: null });
    try {
      const reponse = await dansLaPorteeCourante((appel) =>
        documentsGalerieApi.preparerDocumentsExposition(galerieSlug, exposition.id, appel),
      );
      if (actuelle())
        setPreparation({
          contexte: contextePreparation,
          enCours: true,
          documents: reponse,
          erreur: null,
        });
    } catch (error) {
      if (actuelle() && !estPorteeObsolete(error)) {
        setPreparation({
          contexte: contextePreparation,
          enCours: true,
          documents: null,
          erreur: estApiError(error)
            ? error.message
            : t('regie:expositions.listeDePrix.preparerEchec'),
        });
      }
    } finally {
      if (actuelle()) {
        preparationActive.current = false;
        setPreparation((courante) =>
          courante?.contexte === contextePreparation ? { ...courante, enCours: false } : courante,
        );
      }
    }
  }

  return (
    <div className={styles.page}>
      <Link to={`/${galerieSlug}/expositions`} className={styles.retour}>
        ← {t('regie:expositions.retour')}
      </Link>

      <header className={styles.couverture} data-etat={exposition.etat}>
        <div className={styles.couvertureTexte}>
          <NouvelleSelection
            key={`${compte.id}:${galerieSlug}`}
            accrochage
            titreInitial={texteEnLangue(exposition.titre, langue)}
            origine={{ depuis: { exposition: exposition.id } }}
          />
          <div className={styles.enteteHaut}>
            <Badge tone="neutral">{t(`regie:expositions.types.${exposition.type}`)}</Badge>
            <Badge tone={exposition.visibleSurLeSite ? 'available' : 'neutral'}>
              {exposition.visibleSurLeSite
                ? t('regie:expositions.carte.visible')
                : t('regie:expositions.carte.masquee')}
            </Badge>
          </div>

          <h1 className={styles.titre}>{texteEnLangue(exposition.titre, langue)}</h1>
          {/* Studio : geste absent quand l’add-on ou l’écriture est fermé. */}
          {galerieAModule(galerie, 'studio-contenu') &&
            galerieALaction(galerie, 'studio:write') && (
              <Link to={`/${galerieSlug}/studio/nouveau?exposition=${exposition.id}`}>
                {t('regie:studio.depuisExposition')}
              </Link>
            )}
          <IndicateurFraicheur depuis={fiche.dataUpdatedAt} />

          <p className={styles.meta}>
            <span>
              {formatDateRange(new Date(exposition.debutLe), new Date(exposition.finLe), langue)}
            </span>
            <span className={styles.metaSeparateur} aria-hidden>
              ·
            </span>
            <span className={styles.metaLieu}>
              {exposition.stand
                ? t('regie:expositions.carte.stand', { stand: exposition.stand })
                : (lieu?.nom ?? '—')}
            </span>
          </p>

          <div className={styles.etatLigne}>
            <ol className={styles.parcours} aria-hidden>
              {PARCOURS.map((code) => (
                <li
                  key={code}
                  className={styles.etape}
                  data-position={
                    code === exposition.etat
                      ? 'actuelle'
                      : PARCOURS.indexOf(code) < PARCOURS.indexOf(exposition.etat)
                        ? 'franchie'
                        : 'a-venir'
                  }
                />
              ))}
            </ol>
            <Badge tone={teinteDeLEtat(exposition.etat)}>
              {t(`regie:expositions.etats.${exposition.etat}`)}
            </Badge>
            {peutEcrire ? (
              <DropdownMenu
                align="start"
                entries={entreesEtat}
                trigger={
                  <Button variant="secondary" size="sm">
                    {t('regie:expositions.etatMenu.titre')}
                    <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
                  </Button>
                }
              />
            ) : null}
          </div>
        </div>

        <div className={styles.couvertureVisuel}>
          <VisuelExposition
            galerie={galerieSlug}
            expositionId={exposition.id}
            oeuvres={oeuvres.map((oeuvre) => oeuvre.id)}
            type={exposition.type}
            stand={exposition.stand}
            taille="couverture"
          />
          <span className={styles.couvertureCompte}>
            {t('regie:expositions.carte.oeuvresCompte', { count: oeuvres.length })}
          </span>
        </div>

        {peutTelechargerListe ||
        peutEcrire ||
        galerieALaction(galerie, 'documents:write') ||
        galerieALaction(galerie, 'viewing-rooms:write') ? (
          <div className={styles.gestes}>
            {peutTelechargerListe ? (
              <Button
                variant="primary"
                size="sm"
                loading={listeDePrixPdfEnCours}
                onClick={() => void ouvrirListeDePrixPdf()}
              >
                <Download size={16} strokeWidth={1.5} aria-hidden />
                {t('regie:expositions.listeDePrix.actionPdf')}
              </Button>
            ) : null}
            {peutTelechargerListe ? (
              <Button
                variant="secondary"
                size="sm"
                loading={listeDePrixEnCours}
                onClick={() => void telechargerListeDePrix()}
              >
                <Download size={16} strokeWidth={1.5} aria-hidden />
                {t('regie:expositions.listeDePrix.action')}
              </Button>
            ) : null}
            <PortfolioExposition
              titre={texteEnLangue(exposition.titre, langue)}
              oeuvres={oeuvres}
            />
            {peutPreparer ? (
              <Button
                variant="secondary"
                size="sm"
                loading={preparationEnCours}
                onClick={() => void preparerDocuments()}
              >
                <FileStack size={16} strokeWidth={1.5} aria-hidden />
                {t('regie:expositions.listeDePrix.preparer')}
              </Button>
            ) : null}
            {peutEcrire && exposition.archive ? (
              <Button
                variant="secondary"
                size="sm"
                loading={restaurer.isPending}
                onClick={() =>
                  restaurer.mutate(exposition.id, {
                    onSuccess: () => setRetour(t('regie:expositions.restaurer.succes')),
                  })
                }
              >
                {t('regie:expositions.restaurer.action')}
              </Button>
            ) : peutEcrire ? (
              <Button
                variant="danger"
                size="sm"
                className={styles.gesteSensible}
                onClick={() => setArchivageOuvert(true)}
              >
                {t('regie:expositions.archiver.action')}
              </Button>
            ) : null}
          </div>
        ) : null}
        <RetourGeste message={retour} />
        {restaurer.isError ? (
          <p className={styles.erreur} role="alert">
            {estApiError(restaurer.error) ? restaurer.error.message : t('commun:erreurReseau')}
          </p>
        ) : null}
        {listeDePrixErreur ? (
          <p className={styles.erreur} role="alert">
            {listeDePrixErreur}
          </p>
        ) : null}
        {peutPreparer && preparationErreur ? (
          <p className={styles.erreur} role="alert">
            {preparationErreur}
          </p>
        ) : null}
        {peutPreparer && documentsPrepares ? (
          <p className={styles.confirmation} role="status">
            {modeDemonstrationActif()
              ? `${t('regie:expositions.listeDePrix.preparerSimulation')} `
              : null}
            {t(
              documentsPrepares.enfiles === 0
                ? 'regie:expositions.listeDePrix.preparerAucun'
                : 'regie:expositions.listeDePrix.preparerConfirmation',
              {
                count: documentsPrepares.enfiles,
                demande: t('regie:expositions.listeDePrix.preparerDemande', {
                  count: documentsPrepares.total,
                }),
              },
            )}
          </p>
        ) : null}
        {galerieAModule(galerie, 'pilotage') && galerieALaction(galerie, 'insights:read') && (
          <Link to={`/${galerieSlug}/pilotage/expositions/${exposition.id}`}>
            {t('regie:pilotage.rentabilite')}
          </Link>
        )}
        {galerieAModule(galerie, 'plan-accrochage') && (
          <Link to={`/${galerieSlug}/accrochage?exposition=${exposition.id}`}>
            {t('regie:accrochage.plansExposition')}
          </Link>
        )}
      </header>

      <Tabs
        tabs={[
          {
            value: 'informations',
            label: t('regie:expositions.onglets.informations'),
            content: (
              <InformationsTab
                key={exposition.id}
                galerieSlug={galerieSlug}
                exposition={exposition}
              />
            ),
          },
          {
            value: 'oeuvres-artistes',
            label: t('regie:expositions.onglets.oeuvresArtistes'),
            content: (
              <OeuvresArtistesTab
                galerieSlug={galerieSlug}
                exposition={exposition}
                artistes={artistes}
                oeuvres={oeuvres}
                peutEcrire={peutEcrire}
              />
            ),
          },
          {
            value: 'medias',
            label: t('regie:expositions.onglets.medias'),
            content: (
              <MediasTab
                galerieSlug={galerieSlug}
                expositionId={exposition.id}
                vuesAccrochage={vuesAccrochage}
                peutEcrire={peutEcrire}
              />
            ),
          },
          {
            value: 'actualites',
            label: t('regie:expositions.onglets.actualites'),
            content: (
              <ActualitesTab
                galerieSlug={galerieSlug}
                expositionId={exposition.id}
                peutEcrire={peutEcrire}
              />
            ),
          },
          ...(galerieAModule(galerie, 'pilotage') && galerieALaction(galerie, 'insights:read')
            ? [
                {
                  value: 'pilotage',
                  label: t('regie:pilotage.couts'),
                  content: (
                    <CoutsExposition
                      key={`${identite?.compteId}:${epoque}:${galerieSlug}:${exposition.id}`}
                      expositionId={exposition.id}
                    />
                  ),
                },
              ]
            : []),
          ...(galerieAModule(galerie, 'catalogue-visite')
            ? [
                {
                  value: 'catalogue-visite',
                  label: (
                    <LibelleOnglet module="catalogue-visite">
                      {t('regie:expositions.onglets.catalogueVisite')}
                    </LibelleOnglet>
                  ),
                  content: (
                    <CatalogueVisiteTab
                      key={`${identite?.compteId}:${epoque}:${galerieSlug}:${exposition.id}`}
                      galerieSlug={galerieSlug}
                      expositionId={exposition.id}
                      peutEcrire={peutEcrire && galerieEcritModule(galerie, 'catalogue-visite')}
                    />
                  ),
                },
              ]
            : []),
        ]}
      />

      <Dialog
        open={etatDialogueOuvert}
        onOpenChange={setEtatDialogueOuvert}
        title={t('regie:expositions.etatMenu.confirmerTitre')}
        description={t('regie:expositions.etatMenu.confirmerTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEtatDialogueOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void confirmerEtat()}
              disabled={!etatVise}
              loading={changerEtat.isPending}
            >
              {t('regie:expositions.etatMenu.confirmer')}
            </Button>
          </>
        }
      >
        <div className={styles.formulaireEtat}>
          <Select
            aria-label={t('regie:expositions.etatMenu.etatViseChamp')}
            value={etatVise}
            onValueChange={(valeur) => setEtatVise(valeur as EtatExposition)}
            options={autresEtats(exposition.etat).map((code) => ({
              value: code,
              label: t(`regie:expositions.etats.${code}`),
            }))}
          />
          {erreurEtat ? (
            <p className={styles.erreur} role="alert">
              {erreurEtat}
            </p>
          ) : null}
        </div>
      </Dialog>

      {galerieAModule(galerie, 'evenements') && galerieALaction(galerie, 'events:read') ? (
        <EvenementsExposition exposition={exposition} />
      ) : null}
      {galerieAModule(galerie, 'agenda-presse') && galerieALaction(galerie, 'press:read') ? (
        <SectionPresse galerieSlug={galerieSlug} filtre={{ expositionId: exposition.id }} />
      ) : null}
      <Dialog
        open={archivageOuvert}
        onOpenChange={setArchivageOuvert}
        title={t('regie:expositions.archiver.confirmationTitre')}
        description={t('regie:expositions.archiver.confirmationTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setArchivageOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={archiver.isPending}
              onClick={() => {
                archiver.mutate(
                  { id: exposition.id },
                  {
                    onSuccess: () => {
                      setArchivageOuvert(false);
                      navigate(`/${galerieSlug}/expositions`);
                    },
                  },
                );
              }}
            >
              {t('regie:expositions.archiver.confirmer')}
            </Button>
          </>
        }
      >
        <p>{texteEnLangue(exposition.titre, langue)}</p>
      </Dialog>
    </div>
  );
}
